import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from "docx";
import {
  parseBits,
  getBitTimings,
  formatTime,
  getTotalTimeForBody,
  DEFAULT_WORDS_PER_MINUTE,
} from "./documentBodyUtils";
import { parseJokeLine } from "./DocumentBody";

// Matches the editor's monospace body font (see DocumentBody.js). Courier
// New is used rather than the web font Courier Prime since it's the one
// guaranteed to be installed in Word/Google Docs.
const BODY_FONT = "Courier New";

// Split a bit's content lines into jokes - blocks of lines separated by
// blank lines - mirroring formatJokesToElements in DocumentBody.js.
function splitIntoJokes(lines) {
  const jokes = [];
  let current = [];
  lines.forEach((line) => {
    if (line.trim() === "") {
      if (current.length) {
        jokes.push(current);
        current = [];
      }
      return;
    }
    current.push(line);
  });
  if (current.length) jokes.push(current);
  return jokes;
}

// One joke becomes one paragraph, with a line break between each source
// line, punchlines bolded and stage directions italicized - matching how
// Perform mode renders the same text.
function buildJokeParagraph(lines) {
  const children = [];
  lines.forEach((line, lineIndex) => {
    const parts = parseJokeLine(line);
    parts.forEach((part, partIndex) => {
      const runBreak = lineIndex > 0 && partIndex === 0 ? 1 : undefined;
      if (part.punchline !== undefined) {
        children.push(new TextRun({ text: part.punchline, bold: true, break: runBreak }));
      } else if (part.stage !== undefined) {
        children.push(
          new TextRun({ text: part.stage, italics: true, color: "808080", break: runBreak })
        );
      } else if (part.text !== undefined) {
        children.push(new TextRun({ text: part.text, break: runBreak }));
      }
    });
  });
  return new Paragraph({ spacing: { after: 160 }, children });
}

function buildBitHeaderParagraph(bit, startSeconds, endSeconds, includeTiming) {
  const timeLabel = includeTiming
    ? `${formatTime(startSeconds)}–${formatTime(endSeconds)}`
    : "";
  const label = bit.name && timeLabel ? `${bit.name} - ${timeLabel}` : bit.name || timeLabel;
  return new Paragraph({
    heading: HeadingLevel.HEADING_1,
    children: [new TextRun({ text: label })],
  });
}

// Used when timing is wanted but bit headers aren't - a plain bolded
// time range in place of the heading.
function buildTimingLineParagraph(startSeconds, endSeconds) {
  return new Paragraph({
    spacing: { before: 240, after: 80 },
    children: [
      new TextRun({ text: `${formatTime(startSeconds)}–${formatTime(endSeconds)}`, bold: true }),
    ],
  });
}

// Builds a .docx that mirrors Perform mode: a timed header per bit, and
// its jokes with punchlines bolded and stage directions italicized.
// includeHeaders and includeTiming are independent: headers can show with
// or without times, and times can show as their own line even without
// headers. The font and bolded punchlines always apply.
export async function buildDocxBlob({
  name,
  body,
  wordsPerMinute = DEFAULT_WORDS_PER_MINUTE,
  includeHeaders = true,
  includeTiming = true,
}) {
  const bits = parseBits(body);
  const timings = getBitTimings(bits, wordsPerMinute);

  const children = [];
  bits.forEach((bit, i) => {
    if (bit.name === null && (!bit.content || bit.content.length === 0)) return;
    const timing = timings[i] ?? { start: 0, end: 0 };
    if (includeHeaders) {
      children.push(buildBitHeaderParagraph(bit, timing.start, timing.end, includeTiming));
    } else if (includeTiming) {
      children.push(buildTimingLineParagraph(timing.start, timing.end));
    } else if (children.length > 0) {
      // No header or timing text, but still start a new paragraph where
      // one would have gone, so bits stay visually separated.
      children.push(new Paragraph({}));
    }
    splitIntoJokes(bit.content).forEach((joke) => {
      children.push(buildJokeParagraph(joke));
    });
  });

  if (includeTiming) {
    const totalTime = getTotalTimeForBody(body, wordsPerMinute);
    children.push(
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        spacing: { before: 360 },
        children: [new TextRun({ text: `Total - ${totalTime}`, italics: true, color: "888888" })],
      })
    );
  }

  const doc = new Document({
    title: name || "Set",
    styles: {
      default: {
        document: {
          run: { font: BODY_FONT },
        },
      },
    },
    sections: [{ children }],
  });

  return Packer.toBlob(doc);
}

function sanitizeFilename(name) {
  const trimmed = (name || "").trim().replace(/[\\/:*?"<>|]+/g, "_");
  return trimmed || "set";
}

export async function exportDocumentToDocx({
  name,
  body,
  wordsPerMinute,
  includeHeaders,
  includeTiming,
}) {
  const blob = await buildDocxBlob({ name, body, wordsPerMinute, includeHeaders, includeTiming });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${sanitizeFilename(name)}.docx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
